/**
 * assistSession.js — sessão de conversa COMPARTILHADA do VUKZ Assist.
 *
 * Single Assist (26/08/2026): drawer global, página /assist e widgets de
 * home devem CONTINUAR a mesma conversa — abrir o Assist em outro ponto
 * da aplicação não cria silenciosamente outro histórico.
 *
 * Store singleton em memória, chaveado por role normalizada:
 *   { threadId, messages }
 *
 * Nova conversa SOMENTE via clearSession (botão "Nova conversa") ou
 * regra explícita de isolamento de workflow.
 *
 * Sem dependências — importável por qualquer componente/teste.
 */

const sessions = new Map(); // role -> { threadId, messages }

// Pub/sub (27/08/2026): duas superfícies SIMULTÂNEAS do mesmo Assist
// (drawer global + embedded no builder) precisam refletir a mesma conversa
// ao vivo — uma mensagem enviada em uma atualiza a outra.
const listeners = new Set();

function notify(role, session) {
  listeners.forEach((fn) => {
    try { fn(role, session); } catch { /* listener quebrado não derruba os demais */ }
  });
}

/** Assina mudanças de sessão. Retorna unsubscribe. */
export function subscribeSession(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

const emptySession = () => ({ threadId: null, messages: [] });

export function getSession(role) {
  const key = role || 'ATLETA';
  if (!sessions.has(key)) sessions.set(key, emptySession());
  return sessions.get(key);
}

export function saveSession(role, { threadId, messages }) {
  const key = role || 'ATLETA';
  const prev = getSession(key);
  const next = {
    threadId: threadId !== undefined ? threadId : prev.threadId,
    messages: messages !== undefined ? messages : prev.messages,
  };
  sessions.set(key, next);
  // só notifica quando algo realmente mudou (referência) — corta loops
  if (next.threadId !== prev.threadId || next.messages !== prev.messages) {
    notify(key, next);
  }
}

export function clearSession(role) {
  const key = role || 'ATLETA';
  const next = emptySession();
  sessions.set(key, next);
  notify(key, next);
}

/** Testes: reset total. */
export function _resetAllSessions() {
  sessions.clear();
  listeners.clear();
}
